import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, X } from "lucide-react";
import { normCulture, useCultureLookup } from "@/lib/cultures";

export const Field = ({ label, value, onChange, type = "text", placeholder, required, testId }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Input value={value || ""} onChange={e => onChange(e.target.value)} type={type}
      placeholder={placeholder || label} className="h-9 text-sm bg-white border-black/10 mt-1"
      data-testid={testId} />
  </div>
);

export function DeleteConfirm({ confirming, onConfirm, onCancel, small }) {
  if (!confirming) return null;
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-medium" style={{ color: "#DC2626" }}>Supprimer ?</span>
      <Button size="sm" variant="outline" className={`${small ? 'h-6 text-[9px] px-2' : 'h-7 text-[10px] px-2.5'} rounded-lg border-red-200 text-red-600 hover:bg-red-50`}
        onClick={onConfirm} data-testid="confirm-delete-btn">Oui</Button>
      <Button size="sm" variant="ghost" className={`${small ? 'h-6 w-6' : 'h-7 w-7'}`} onClick={onCancel}>
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
}

export function DeleteButton({ onClick, small, testId }) {
  return (
    <Button size="sm" variant="ghost" className={`${small ? 'h-6 w-6' : 'h-7 w-7'} text-red-400 hover:text-red-600 shrink-0`}
      onClick={onClick} data-testid={testId}>
      <Trash2 className={`${small ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
    </Button>
  );
}

/** « 3.0 L/ha » → { value: "3", unit: "L/ha" } ; un nombre seul donne unit "". */
export function splitDose(raw) {
  if (raw == null || raw === "") return null;
  const m = String(raw).replace(",", ".").match(/^\s*(\d+(?:\.\d+)?)\s*(.*?)\s*$/);
  if (!m) return null;
  let unit = m[2];
  if (unit === "t/ha") unit = "T/ha";
  return { value: String(parseFloat(m[1])), unit };
}

/**
 * Dose recommandée d'un produit pour la culture de la parcelle.
 * - Produits de base (liste Véyèr CIEL) : dose par culture de l'appli, sinon dose par défaut.
 * - Produits E-Phy (matières fertilisantes) : leurs doses sont données par grand groupe de cultures ; on prend
 *   le premier groupe de la culture (référentiel, champ groupes_mfsc) qui a une dose. Sans groupe correspondant,
 *   aucune dose n'est proposée, sauf si elle est la même pour tous les groupes.
 * Retourne { raw, label } : raw = dose brute (« 3.0 L/ha » ou 3), label = origine affichée sous la case.
 */
export function pickRecommendedDose(product, cultureType, culture) {
  const dc = product?.doses_culture || {};
  const names = new Set([cultureType, culture?.libelle, ...(culture?.synonymes || [])].filter(Boolean).map(normCulture));
  const own = Object.keys(dc).find((k) => names.has(normCulture(k)));
  if (own) return { raw: dc[own], label: null };
  const isEphy = String(product?.amm || "").startsWith("AMM MFSC");
  if (!isEphy) return { raw: product?.dose_default ?? null, label: null };
  const group = (culture?.groupes_mfsc || []).find((g) => dc[g]);
  if (group) return { raw: dc[group], label: `Dose E-Phy pour « ${group} »` };
  const values = [...new Set(Object.values(dc))];
  if (values.length === 1) return { raw: values[0], label: "Dose E-Phy (la même pour toutes les cultures)" };
  if (!values.length) return { raw: null, label: "Pas de dose dans E-Phy pour ce produit : voir l'étiquette" };
  const examples = Object.entries(dc).slice(0, 3).map(([g, v]) => `${g} : ${v}`).join(" · ");
  return { raw: null, label: `Pas de dose E-Phy pour ce type de culture, voir l'étiquette (${examples}…)` };
}

/** showDose=false : fiche produit sans dose recommandée (fertilisation). */
export function ProductInfoPanel({ product, cultureType, surfaceHa, showDose = true }) {
  const cultureOf = useCultureLookup();
  if (!product) return null;
  const rec = showDose ? pickRecommendedDose(product, cultureType, cultureOf(cultureType)) : { raw: null, label: null };
  const d = splitDose(rec.raw);
  if (!d && !rec.label && !product.amm && !product.info && !product.formule) return null;
  const unitBase = (d && d.unit) || product.unit || "";
  const perHa = unitBase.endsWith("/ha");
  const totalDose = d && perHa && surfaceHa ? parseFloat(d.value) * surfaceHa : null;
  const unitTotal = unitBase.replace("/ha", "");
  const doseForCulture = d ? d.value : null;

  return (
    <div className="p-3 rounded-lg border space-y-2" style={{ background: "#F0FDF4", borderColor: "#2D6A4F30" }} data-testid="product-info-panel">
      {product.amm && (
        <div className="flex items-start gap-2">
          <Badge className="text-[9px] border-0 px-1.5 py-0.5 shrink-0" style={{ background: "#2D6A4F20", color: "#2D6A4F" }}>AMM</Badge>
          <span className="text-[11px] font-medium" style={{ color: "#1A1A1A" }} data-testid="product-amm">{product.amm}</span>
        </div>
      )}
      {product.formule && (
        <div className="flex items-start gap-2">
          <Badge className="text-[9px] border-0 px-1.5 py-0.5 shrink-0" style={{ background: "#2D6A4F20", color: "#2D6A4F" }}>Formule</Badge>
          <span className="text-[11px] font-medium" style={{ color: "#1A1A1A" }} data-testid="product-formule">{product.formule}</span>
        </div>
      )}
      {doseForCulture != null && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-medium" style={{ color: "#4A4A4A" }}>
            Dose recommandée {cultureType ? `(${cultureType})` : "(par défaut)"}
          </span>
          <span className="text-sm font-bold" style={{ color: "#2D6A4F" }} data-testid="product-dose-rec">
            {doseForCulture} {unitBase}
          </span>
        </div>
      )}
      {rec.label && <p className="text-[10px]" style={{ color: d ? "#2D6A4F" : "#9A6A12" }} data-testid="product-dose-source">{rec.label}</p>}
      {totalDose != null && surfaceHa > 0 && (
        <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: "#2D6A4F20" }}>
          <span className="text-[10px] font-medium" style={{ color: "#4A4A4A" }}>
            Dose totale ({surfaceHa} ha)
          </span>
          <span className="text-sm font-bold" style={{ color: "#E07A5F" }} data-testid="product-dose-total">
            {Math.round(totalDose * 100) / 100} {unitTotal}
          </span>
        </div>
      )}
      {product.info && (
        <p className="text-[10px] italic leading-relaxed" style={{ color: "#6B7280" }} data-testid="product-info-text">{product.info}</p>
      )}
    </div>
  );
}
